import { MaterialCommunityIcons } from "@expo/vector-icons";
import { Box, Button, Divider, HStack, Icon, Text, VStack } from "native-base";
import React from "react";
import { Linking, Platform } from "react-native";

import ExternalImage from "@/components/atoms/ExternalImage";
import Modal from "@/components/molecules/Modal";
import { useStorePaymentMethods } from "@/hooks/domain/stores";
import { Store } from "@/interfaces/domain/Store";
import { StorePaymentMethod } from "@/interfaces/domain/StorePaymentMethod";

type StoreProfileModalProps = {
  isOpen: boolean;
  store: Store;
  onClose: () => void;
};

const OpeningHoursList = ({ store }: { store: Store }) => {
  const sorted = [...(store?.horario_funcionamento || [])]?.sort((a, b) =>
    a.num_dia > b.num_dia ? 1 : -1
  );
  return (
    <VStack>
      {sorted.map((hour) => {
        const firstHour = hour?.horarios[0];
        const secontHour = hour?.horarios[hour?.horarios?.length - 1];
        if (!firstHour || !secontHour) return null;
        return (
          <HStack key={hour._id} py="1">
            <Text bold>{hour.desc_dia}: </Text>
            <Text>
              das{" "}
              <Text color="primary.600" bold>
                {firstHour.hora_inicio}h{" "}
              </Text>
            </Text>
            <Text>
              às{" "}
              <Text color="primary.600" bold>
                {secontHour.hora_fim}h{" "}
              </Text>
            </Text>
          </HStack>
        );
      })}
      <Divider />
    </VStack>
  );
};

const PaymentMethodsList = ({ paymentMethods }: { paymentMethods: StorePaymentMethod[] }) => {
  const debit = paymentMethods?.filter((c) => c.tipo === "debito");
  const credit = paymentMethods?.filter((c) => c.tipo === "credito");

  const renderPaymentItem = (item: StorePaymentMethod) => (
    <Box key={item._id} p="4px" m="2px" borderWidth="1px" borderColor="gray.400" rounded="lg">
      <ExternalImage source={{ uri: item?.logo }} width={45} height={35} contentFit="cover" />
    </Box>
  );

  return (
    <VStack>
      <Text>À vista</Text>
      <HStack py="1" flexWrap="wrap">
        {debit?.map((item) => renderPaymentItem(item))}
      </HStack>
      <Text>À prazo</Text>
      <HStack py="1" flexWrap="wrap">
        {credit?.map((item) => renderPaymentItem(item))}
      </HStack>
      <Divider />
    </VStack>
  );
};

const DeliveryAreasList = ({ store }: { store: Store }) => {
  return (
    <VStack>
      {store?.area_cobertura?.map((area) => (
        <Text key={area._id} color="text.600">
          {area.bairro}, {area.cidade}
        </Text>
      ))}
      <Divider />
    </VStack>
  );
};

const StoreProfileModal = ({ isOpen, store, onClose }: StoreProfileModalProps) => {
  const { data: paymentMethods } = useStorePaymentMethods(store?._id, {
    enabled: !!store?._id,
  });

  const onPressContact = () => {
    const phone = store?.telefone?.replace(" ", "");
    if (Platform.OS === "ios") {
      Linking.openURL(`telprompt:${phone}`);
    } else {
      Linking.openURL(`tel:${phone}`);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      Header={store?.nome_fantasia}
      Body={
        <VStack mb="20">
          <Box rounded="lg" bgColor="rgba(0,0,0,.2)">
            <ExternalImage
              width="100%"
              height={120}
              borderRadius={20}
              source={{ uri: store?.informacoes_adicionais?.banner }}
            />
          </Box>
          <HStack p="2" alignItems="center" justifyContent="space-between">
            <Text flex={0.6} fontSize="lg" color="text.600" bold>
              {store?.informacoes_adicionais?.descricao}
            </Text>
            <Button
              colorScheme="green"
              leftIcon={<Icon as={<MaterialCommunityIcons name="whatsapp" />}></Icon>}
              onPress={onPressContact}>
              Contato
            </Button>
          </HStack>
          <Divider />
          <VStack p="2">
            <Text py="2" fontSize="lg" color="text.600" bold>
              Horário de funcionamento
            </Text>
            <OpeningHoursList store={store} />
          </VStack>
          {!!paymentMethods && (
            <VStack p="2">
              <Text py="2" fontSize="lg" color="text.600" bold>
                Métodos de pagamento
              </Text>
              <PaymentMethodsList paymentMethods={paymentMethods} />
            </VStack>
          )}
          <VStack p="2">
            <Text py="2" fontSize="lg" color="text.600" bold>
              Áreas de entrega
            </Text>
            <DeliveryAreasList store={store} />
          </VStack>
        </VStack>
      }
    />
  );
};

export default StoreProfileModal;
